// frontend/src/components/Diario/ModalHigiene.jsx
// Modal completo para registro de higiene

import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { ShowerHead, Save, X, ChevronDown } from 'lucide-react';
import Modal from '../UI/Modal';
import DropdownMenu from '../UI/DropdownMenu.jsx';

const TURNOS = ['Manhã', 'Tarde', 'Noite', 'Madrugada'];

const TIPOS_HIGIENE = [
    'Banho no Leito',
    'Higiene Oral',
    'Troca de Fralda',
    'Lavagem de Cabelo',
    'Troca de Roupas',
    'Cuidados com Unhas',
    'Outro'
];

/**
 * Modal completo para registro de higiene
 */
const ModalHigiene = ({ isOpen, onClose, pacienteId, onCreateRegistro, onRegistroSucesso }) => {
    const [formData, setFormData] = useState({
        tipo_higiene: '',
        observacoes: '',
        turno: 'Manhã'
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        
        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.tipo_higiene.trim()) {
            newErrors.tipo_higiene = 'Tipo de higiene é obrigatório';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            const dataToSend = {
                paciente_id: pacienteId,
                tipo_registro: 'Higienizacao',
                observacoes: formData.observacoes || null,
                turno: formData.turno,
                detalhes: {
                    tipo_higiene: formData.tipo_higiene
                }
            };

            await onCreateRegistro(dataToSend);
            toast.success('Registro de higiene salvo com sucesso!');
            
            if (onRegistroSucesso) {
                onRegistroSucesso();
            }
            
            // Resetar formulário
            setFormData({
                tipo_higiene: '',
                observacoes: '',
                turno: 'Manhã'
            });
            setErrors({});
            onClose();
        } catch (error) {
            toast.error(error.message || 'Erro ao registrar higiene');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            setFormData({
                tipo_higiene: '',
                observacoes: '',
                turno: 'Manhã'
            });
            setErrors({});
            onClose();
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} title="Registrar Higiene" size="lg">
            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Tipo de Higiene */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        <ShowerHead size={16} className="inline mr-2" />
                        Tipo de Higiene <span className="text-red-500">*</span>
                    </label>
                    <DropdownMenu
                        trigger={
                            <button
                                type="button"
                                className={`
                                    mt-1 w-full flex items-center justify-between
                                    border rounded-md shadow-sm p-3 text-sm
                                    focus:ring-emerald-500 focus:border-emerald-500
                                    ${errors.tipo_higiene ? 'border-red-300' : 'border-gray-300'}
                                    ${formData.tipo_higiene ? 'text-gray-900' : 'text-gray-500'}
                                `}
                            >
                                <span>{formData.tipo_higiene || 'Selecione o tipo de higiene...'}</span>
                                <ChevronDown size={18} className="text-gray-400" />
                            </button>
                        }
                        items={TIPOS_HIGIENE.map(tipo => ({
                            id: tipo,
                            label: tipo,
                            onClick: () => {
                                setFormData(prev => ({ ...prev, tipo_higiene: tipo }));
                                if (errors.tipo_higiene) {
                                    setErrors(prev => {
                                        const newErrors = { ...prev };
                                        delete newErrors.tipo_higiene;
                                        return newErrors;
                                    });
                                }
                            }
                        }))}
                        position="bottom-left"
                        className="w-full"
                    />
                    {errors.tipo_higiene && (
                        <p className="mt-1 text-xs text-red-600">{errors.tipo_higiene}</p>
                    )}
                </div>

                {/* Turno */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Turno <span className="text-red-500">*</span>
                    </label>
                    <DropdownMenu
                        trigger={
                            <button
                                type="button"
                                className="mt-1 w-full flex items-center justify-between border border-gray-300 rounded-md shadow-sm p-3 text-sm focus:ring-emerald-500 focus:border-emerald-500 text-gray-900"
                            >
                                <span>{formData.turno}</span>
                                <ChevronDown size={18} className="text-gray-400" />
                            </button>
                        }
                        items={TURNOS.map(turno => ({
                            id: turno,
                            label: turno,
                            onClick: () => {
                                setFormData(prev => ({ ...prev, turno }));
                            }
                        }))}
                        position="bottom-left"
                        className="w-full"
                    />
                </div>

                {/* Observações */}
                <div>
                    <label htmlFor="observacoes" className="block text-sm font-medium text-gray-700 mb-2">
                        Observações (Opcional)
                    </label>
                    <textarea
                        name="observacoes"
                        id="observacoes"
                        value={formData.observacoes}
                        onChange={handleChange}
                        placeholder="Adicione observações sobre a higiene realizada..."
                        rows="4"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-3 text-sm focus:ring-emerald-500 focus:border-emerald-500"
                    />
                </div>

                {/* Botões */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                    <button
                        type="button"
                        onClick={handleClose}
                        disabled={loading}
                        className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
                    >
                        <X size={18} />
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        disabled={loading}
                        className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-md transition disabled:opacity-50"
                    >
                        {loading ? (
                            <>Salvando...</>
                        ) : (
                            <>
                                <Save size={18} />
                                Registrar Higiene
                            </>
                        )}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default ModalHigiene;

